/**
 * Web HTTP Adapter - Browser fetch implementation
 * @module @one-payments/adapters-web
 */

import type { HttpAdapter } from '@one-payments/core';

/**
 * Helper function to extract error message from response
 */
async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    // Try to extract message from various common error response structures
    return data.message || data.error?.message || data.error || response.statusText;
  } catch {
    // If JSON parsing fails, use status text
    return response.statusText;
  }
}

/**
 * Web HTTP Adapter using fetch API with strict generics
 */
export class WebHttpAdapter implements HttpAdapter {
  public async get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    });

    if (!response.ok) {
      const errorMessage = await extractErrorMessage(response);
      throw new Error(errorMessage);
    }

    return (await response.json()) as T;
  }

  public async post<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorMessage = await extractErrorMessage(response);
      throw new Error(errorMessage);
    }

    return (await response.json()) as T;
  }

  public async put<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorMessage = await extractErrorMessage(response);
      throw new Error(errorMessage);
    }

    return (await response.json()) as T;
  }

  public async patch<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorMessage = await extractErrorMessage(response);
      throw new Error(errorMessage);
    }

    return (await response.json()) as T;
  }

  public async delete<T>(url: string, headers?: Record<string, string>): Promise<T> {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    });

    if (!response.ok) {
      const errorMessage = await extractErrorMessage(response);
      throw new Error(errorMessage);
    }

    return (await response.json()) as T;
  }
}
